import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, FileSpreadsheet, Building2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { PageHeader, BRANCH_LABEL, SortTh, useLocalSort } from "../../components/common";
import { EmployeeDialog } from "./EmployeeDialog";
import { ImportDialog } from "./ImportDialog";
import { DepartmentsDialog } from "./DepartmentsDialog";
import { LandingStar } from "../../components/WeeklyStar";

const StatusBtn = ({ e, onDone }) => {
  const suspended = e.status === "suspended";
  const go = () => {
    const reason = window.prompt(suspended ? `Reactivate ${e.name}? Add a note (optional).` : `Suspend ${e.name} (ID ${e.emp_code})? Their app login stops straight away. Reason:`);
    if (reason === null) return;
    api.post(`/hr/employees/${encodeURIComponent(e.emp_code)}/${suspended ? "reactivate" : "suspend"}`, { reason })
      .then(() => { toast.success(suspended ? "Employee reactivated" : "Employee suspended"); onDone(); }).catch((x) => toast.error(errMsg(x)));
  };
  return <button data-testid={`employee-status-${e.emp_code}`} onClick={go} className={`border text-xs px-2 py-1 ${suspended ? "hover:border-emerald-500" : "hover:border-rose-400"}`}>{suspended ? "Reactivate" : "Suspend"}</button>;
};

const Row = ({ e, canEdit, canSuspend, onEdit, onDone }) => (
  <tr data-testid={`employee-row-${e.emp_code}`}>
    <td className="font-mono text-xs">{e.emp_code}</td>
    <td><div className="font-medium">{e.name}</div><div className="text-[11px] text-muted-foreground">{e.official_email || "no email"}</div></td>
    <td className="text-xs">{e.designation || "—"}<div className="text-muted-foreground">{e.department || ""}</div></td>
    <td className="text-xs">{BRANCH_LABEL[e.branch] || e.branch || "—"}</td>
    <td className="text-xs">{e.phone || "—"}</td>
    <td className="num text-xs">{e.joined_on || "—"}</td>
    <td className="text-xs" data-testid={`employee-login-${e.emp_code}`}>{e.login ? e.login.role_name : <span className="text-muted-foreground">No login</span>}</td>
    <td className={`text-xs ${e.status === "active" ? "text-emerald-600" : "text-rose-600"}`} data-testid={`employee-state-${e.emp_code}`}>{e.status}</td>
    <td><div className="flex gap-1">
      {canEdit && <button data-testid={`employee-edit-${e.emp_code}`} onClick={() => onEdit(e)} className="border text-xs px-2 py-1 hover:border-primary">Open</button>}
      {canSuspend && <StatusBtn e={e} onDone={onDone} />}
    </div></td>
  </tr>
);

export default function Employees() {
  const { can } = useAuth();
  if (!can("hr", "read")) return <div className="ax-panel p-6 text-sm" data-testid="employees-locked">Only HR and founders can open Employee records.</div>;
  return <EmployeesInner />;
}

function EmployeesInner() {
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ q: "", status: "" });
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const [depts, setDepts] = useState(false);
  const [sorted, sort] = useLocalSort(rows, "emp_code");
  const load = useCallback(() => api.get("/hr/employees", { params: f }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))), [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <PageHeader eyebrow="People" title="Employee records" desc="HR keeps each employee's details here, gives them an app login with the right role, and can suspend an employee ID. Suspending ends their app sessions straight away.">
        <div className="flex flex-wrap gap-2">
          {["founder", "hr"].includes(user?.role_key) && <button data-testid="departments-button" onClick={() => setDepts(true)} className="border h-9 px-4 text-sm flex items-center gap-2 hover:border-primary"><Building2 className="h-4 w-4" /> Departments</button>}
          {can("hr", "create") && <>
          <button data-testid="import-employees-button" onClick={() => setImp(true)} className="border h-9 px-4 text-sm flex items-center gap-2 hover:border-primary"><FileSpreadsheet className="h-4 w-4" /> Import from Excel</button>
          <button data-testid="add-employee-button" onClick={() => setEdit({})} className="bg-primary text-primary-foreground h-9 px-4 text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add employee</button></>}
        </div>
      </PageHeader>
      <LandingStar />
      <div className="flex flex-wrap gap-2 mb-4">
        <input data-testid="employee-search" placeholder="Search ID, name, email, designation… (Enter)" onKeyDown={(e) => e.key === "Enter" && setF({ ...f, q: e.target.value })} className="bg-muted/60 border h-9 px-2 text-sm flex-1 min-w-[220px]" />
        <select data-testid="employee-status-filter" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="bg-muted/60 border h-9 px-2 text-sm">
          <option value="">All employees</option><option value="active">Active</option><option value="suspended">Suspended</option></select>
      </div>
      <div className="ax-panel ax-scroll ax-freeze"><table className="ax-table w-full min-w-[960px]" data-testid="employees-table">
        <thead><tr>{[["emp_code", "Emp ID"], ["name", "Name"], ["designation", "Designation"], ["branch", "Branch"], [null, "Phone"], ["joined_on", "Joined"], [null, "App access"], ["status", "Status"]].map(([k, l]) => <SortTh key={l} k={k} label={l} sort={sort} />)}<th /></tr></thead>
        <tbody>{sorted.map((e) => <Row key={e.emp_code} e={e} canEdit={can("hr", "read")} canSuspend={can("hr", "delete")} onEdit={setEdit} onDone={load} />)}
          {!rows.length && <tr><td colSpan={9} className="text-sm text-muted-foreground py-8 text-center" data-testid="employees-empty">No employees yet. Add your first one.</td></tr>}</tbody>
      </table></div>
      {imp && <ImportDialog onClose={() => setImp(false)} onDone={load} />}
      {depts && <DepartmentsDialog onClose={() => { setDepts(false); load(); }} />}
      {edit && <EmployeeDialog code={edit.emp_code} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
